import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft, Video, Phone, Calendar as CalendarIcon, Clock, 
    MessageSquare, ExternalLink, Loader2, CheckCircle2, Home, MapPin,
    Stethoscope, UserCheck, AlertCircle, X, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
];

const videoPlatforms = [
    { id: 'google_meet', name: 'Google Meet', icon: '🎥' },
    { id: 'teams', name: 'Microsoft Teams', icon: '💼' },
    { id: 'whatsapp', name: 'WhatsApp Video', icon: '📱' }
];

const consultationTypes = [
    { id: 'video', name: 'Video Call', icon: Video, description: 'Face-to-face consultation via video', price: 'Included' },
    { id: 'phone', name: 'Phone Call', icon: Phone, description: 'Voice consultation over the phone', price: 'Included' },
    { id: 'home_visit', name: 'Doctor Call Out', icon: Home, description: 'Doctor visits you at home', price: 'Price varies' }
];

const serviceTypes = [
    { id: 'consultation', name: 'Medical Consultation', description: 'General medical advice and consultation' },
    { id: 'home_visit', name: 'Home Visit', description: 'Healthcare provider visits you at home' },
    { id: 'nursing_care', name: 'Nursing Care', description: 'Professional nursing services' },
    { id: 'physiotherapy', name: 'Physiotherapy', description: 'Physical therapy and rehabilitation' }
];

const professionTypes = [
    { id: 'doctor', name: 'Doctor' },
    { id: 'nurse', name: 'Nurse' },
    { id: 'healthcare_assistant', name: 'Healthcare Assistant' },
    { id: 'physiotherapist', name: 'Physiotherapist' }
];

const specialtyOptions = [
    'General Practice', 'Internal Medicine', 'Pediatrics', 'Geriatrics',
    'Cardiology', 'Dermatology', 'Orthopedics', 'Neurology', 'Psychiatry'
];

const urgencyLevels = [
    { id: 'normal', name: 'Normal', description: 'Within 24-48 hours', color: 'bg-blue-500' },
    { id: 'urgent', name: 'Urgent', description: 'Within 4-6 hours', color: 'bg-amber-500' },
    { id: 'emergency', name: 'Emergency', description: 'ASAP (higher cost)', color: 'bg-red-500' }
];

export default function Consultations() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [consultations, setConsultations] = useState([]);
    const [showBooking, setShowBooking] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [formData, setFormData] = useState({
        consultation_type: 'video',
        preferred_time: '',
        reason: '',
        video_platform: 'google_meet',
        address: ''
    });

    // Service Request State
    const [showServiceRequest, setShowServiceRequest] = useState(false);
    const [serviceRequests, setServiceRequests] = useState([]);
    const [serviceLoading, setServiceLoading] = useState(false);
    const [serviceDate, setServiceDate] = useState(null);
    const [serviceForm, setServiceForm] = useState({
        service_type: 'consultation',
        specialty_required: '',
        profession_required: '',
        description: '',
        urgency: 'normal',
        preferred_time: '',
        address: '',
        postcode: ''
    });

    useEffect(() => {
        fetchConsultations();
        fetchServiceRequests();
    }, []);

    const fetchServiceRequests = async () => {
        try {
            const response = await axios.get(`${API}/my-service-requests`);
            setServiceRequests(response.data);
        } catch (err) {
            console.error('Failed to fetch service requests');
        }
    };

    const fetchConsultations = async () => {
        try {
            const response = await axios.get(`${API}/consultations`);
            setConsultations(response.data);
        } catch (err) {
            console.error('Failed to fetch consultations');
        }
    };

    const handleServiceRequest = async () => {
        if (!serviceForm.description.trim()) {
            toast.error('Please describe your healthcare need');
            return;
        }
        if (!serviceForm.address.trim()) {
            toast.error('Please enter your address');
            return;
        }

        setServiceLoading(true);
        try {
            const requestData = {
                service_type: serviceForm.service_type,
                specialty_required: serviceForm.specialty_required || null,
                profession_required: serviceForm.profession_required || null,
                description: serviceForm.description,
                urgency: serviceForm.urgency,
                preferred_date: serviceDate ? serviceDate.toISOString().split('T')[0] : null,
                preferred_time: serviceForm.preferred_time || null,
                location: {
                    address: serviceForm.address,
                    postcode: serviceForm.postcode
                }
            };

            const response = await axios.post(`${API}/service-request`, requestData);
            toast.success(`Request submitted! ${response.data.matched_providers_count} providers notified.`);
            setShowServiceRequest(false);
            setServiceForm({
                service_type: 'consultation',
                specialty_required: '',
                profession_required: '',
                description: '',
                urgency: 'normal',
                preferred_time: '',
                address: '',
                postcode: ''
            });
            setServiceDate(null);
            fetchServiceRequests();
        } catch (err) {
            if (err.response?.status === 404) {
                toast.error('No matching providers available. Please try different options.');
            } else {
                toast.error(err.response?.data?.detail || 'Failed to submit request');
            }
        } finally {
            setServiceLoading(false);
        }
    };

    const cancelServiceRequest = async (requestId) => {
        try {
            await axios.post(`${API}/service-request/${requestId}/cancel`);
            toast.success('Request cancelled');
            fetchServiceRequests();
        } catch (err) {
            toast.error('Failed to cancel request');
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { label: 'Finding Provider', color: 'bg-amber-500/20 text-amber-600' },
            accepted: { label: 'Provider Assigned', color: 'bg-green-500/20 text-green-600' },
            in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-600' },
            completed: { label: 'Completed', color: 'bg-slate-500/20 text-slate-600' },
            cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-600' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Badge className={config.color}>{config.label}</Badge>;
    };

    const handleBook = async () => {
        if (!selectedDate || !formData.preferred_time || !formData.reason) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Require address for home visits
        if (formData.consultation_type === 'home_visit' && !formData.address.trim()) {
            toast.error('Please enter your address for home visit');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API}/consultations/book`, {
                ...formData,
                preferred_date: selectedDate.toISOString().split('T')[0]
            });
            toast.success('Consultation booked successfully!');
            setShowBooking(false);
            setFormData({
                consultation_type: 'video',
                preferred_time: '',
                reason: '',
                video_platform: 'google_meet',
                address: ''
            });
            setSelectedDate(null);
            fetchConsultations();
            
            if (response.data.video_link) {
                window.open(response.data.video_link, '_blank');
            }
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('Premium subscription required for consultations');
                navigate('/subscription');
            } else {
                toast.error('Failed to book consultation');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="consultations-page">
            <div className="max-w-4xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
                
                {/* Centered Header Section */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Video className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold">Consultations</h1>
                    </div>
                    <p className="text-muted-foreground mb-4">Book consultations or request a healthcare provider</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button onClick={() => setShowBooking(true)} className="rounded-full">
                            <CalendarIcon className="w-4 h-4 mr-2" /> Book Consultation
                        </Button>
                        <Button onClick={() => setShowServiceRequest(true)} variant="outline" className="rounded-full border-teal-500 text-teal-600 hover:bg-teal-50">
                            <Stethoscope className="w-4 h-4 mr-2" /> Request Provider
                        </Button>
                    </div>
                </div>

                {showBooking ? (
                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle>Book New Consultation</CardTitle>
                            <CardDescription>Schedule a consultation with a healthcare provider</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Consultation Type Selection */}
                            <div className="space-y-3">
                                <Label className="text-base font-medium">Consultation Type</Label>
                                <div className="grid sm:grid-cols-3 gap-3">
                                    {consultationTypes.map((type) => {
                                        const Icon = type.icon;
                                        const isSelected = formData.consultation_type === type.id;
                                        return (
                                            <button
                                                key={type.id}
                                                type="button"
                                                onClick={() => setFormData({...formData, consultation_type: type.id})}
                                                className={`p-4 rounded-xl border-2 text-left transition-all
                                                    ${isSelected 
                                                        ? 'border-primary bg-primary/5 shadow-md' 
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'}`}
                                            >
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                                                        ${isSelected ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{type.name}</p>
                                                        <p className="text-xs text-primary">{type.price}</p>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-muted-foreground">{type.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    {formData.consultation_type === 'video' && (
                                        <div className="space-y-2">
                                            <Label>Video Platform</Label>
                                            <Select value={formData.video_platform} onValueChange={(v) => setFormData({...formData, video_platform: v})}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {videoPlatforms.map(p => (
                                                        <SelectItem key={p.id} value={p.id}>{p.icon} {p.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    {formData.consultation_type === 'home_visit' && (
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <MapPin className="w-4 h-4 text-primary" />
                                                Visit Address
                                            </Label>
                                            <Textarea 
                                                value={formData.address} 
                                                onChange={(e) => setFormData({...formData, address: e.target.value})}
                                                placeholder="Enter your full address including postcode/zip code..."
                                                rows={3}
                                            />
                                            <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
                                                ⚠️ Pricing varies based on time of day, day of week, and distance. You'll receive a quote before confirmation.
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label>Preferred Time</Label>
                                        <Select value={formData.preferred_time} onValueChange={(v) => setFormData({...formData, preferred_time: v})}>
                                            <SelectTrigger><SelectValue placeholder="Select time" /></SelectTrigger>
                                            <SelectContent>
                                                {timeSlots.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Reason for Consultation</Label>
                                        <Textarea 
                                            value={formData.reason} 
                                            onChange={(e) => setFormData({...formData, reason: e.target.value})}
                                            placeholder="Briefly describe the reason for your consultation..."
                                            rows={4}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Select Date</Label>
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={setSelectedDate}
                                        disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                                        className="rounded-md border"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowBooking(false)}>Cancel</Button>
                                <Button onClick={handleBook} disabled={loading} className="rounded-full">
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                    Confirm Booking
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : null}

                {/* Service Request Form */}
                {showServiceRequest && (
                    <Card className="mb-8 border-teal-200 dark:border-teal-800">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Stethoscope className="w-5 h-5 text-teal-500" />
                                        Request Healthcare Provider
                                    </CardTitle>
                                    <CardDescription>Find a verified healthcare provider matched to your needs</CardDescription>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => setShowServiceRequest(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Service Type Selection */}
                            <div className="space-y-3">
                                <Label className="text-base font-medium">What do you need?</Label>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {serviceTypes.map((type) => {
                                        const isSelected = serviceForm.service_type === type.id;
                                        return (
                                            <button
                                                key={type.id}
                                                type="button"
                                                onClick={() => setServiceForm({...serviceForm, service_type: type.id})}
                                                className={`p-4 rounded-xl border-2 text-left transition-all
                                                    ${isSelected 
                                                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-teal-300'}`}
                                            >
                                                <p className="font-medium">{type.name}</p>
                                                <p className="text-xs text-muted-foreground">{type.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Urgency Selection */}
                            <div className="space-y-3">
                                <Label className="text-base font-medium">How urgent is this?</Label>
                                <div className="grid sm:grid-cols-3 gap-3">
                                    {urgencyLevels.map((level) => {
                                        const isSelected = serviceForm.urgency === level.id;
                                        return (
                                            <button
                                                key={level.id}
                                                type="button"
                                                onClick={() => setServiceForm({...serviceForm, urgency: level.id})}
                                                className={`p-3 rounded-xl border-2 text-left transition-all
                                                    ${isSelected 
                                                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-teal-300'}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${level.color}`} />
                                                    <p className="font-medium text-sm">{level.name}</p>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">{level.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    {/* Provider Preference */}
                                    <div className="space-y-2">
                                        <Label>Provider Type (Optional)</Label>
                                        <Select 
                                            value={serviceForm.profession_required || "any"} 
                                            onValueChange={(v) => setServiceForm({...serviceForm, profession_required: v === "any" ? "" : v})}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Any provider type" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="any">Any provider type</SelectItem>
                                                {professionTypes.map(p => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Specialty */}
                                    <div className="space-y-2">
                                        <Label>Specialty (Optional)</Label>
                                        <Select 
                                            value={serviceForm.specialty_required || "any"} 
                                            onValueChange={(v) => setServiceForm({...serviceForm, specialty_required: v === "any" ? "" : v})}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Any specialty" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="any">Any specialty</SelectItem>
                                                {specialtyOptions.map(s => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Preferred Time */}
                                    <div className="space-y-2">
                                        <Label>Preferred Time (Optional)</Label>
                                        <Select 
                                            value={serviceForm.preferred_time || "any"} 
                                            onValueChange={(v) => setServiceForm({...serviceForm, preferred_time: v === "any" ? "" : v})}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Any time" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="any">Any time</SelectItem>
                                                {timeSlots.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Address */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-teal-500" />
                                            Your Location *
                                        </Label>
                                        <Textarea 
                                            value={serviceForm.address}
                                            onChange={(e) => setServiceForm({...serviceForm, address: e.target.value})}
                                            placeholder="Enter your full address..."
                                            rows={2}
                                        />
                                        <Input 
                                            value={serviceForm.postcode}
                                            onChange={(e) => setServiceForm({...serviceForm, postcode: e.target.value})}
                                            placeholder="Postcode/ZIP"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* Preferred Date */}
                                    <div className="space-y-2">
                                        <Label>Preferred Date (Optional)</Label>
                                        <Calendar
                                            mode="single"
                                            selected={serviceDate}
                                            onSelect={setServiceDate}
                                            disabled={(date) => date < new Date()}
                                            className="rounded-md border"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <Label className="text-base font-medium">Describe your healthcare need *</Label>
                                <Textarea 
                                    value={serviceForm.description}
                                    onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                                    placeholder="Please describe your symptoms, condition, or the type of care you need. Include any relevant medical history..."
                                    rows={4}
                                />
                            </div>

                            {/* Info Box */}
                            <div className="p-4 bg-teal-50 dark:bg-teal-950/30 rounded-xl">
                                <div className="flex gap-3">
                                    <AlertCircle className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-medium text-teal-700 dark:text-teal-300">How it works</p>
                                        <ul className="text-teal-600 dark:text-teal-400 mt-1 space-y-1">
                                            <li>• We'll match you with verified healthcare providers in your area</li>
                                            <li>• Providers will be notified and can accept your request</li>
                                            <li>• You'll receive confirmation when a provider accepts</li>
                                            <li>• Pricing depends on service type, urgency, and distance</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowServiceRequest(false)}>Cancel</Button>
                                <Button 
                                    onClick={handleServiceRequest} 
                                    disabled={serviceLoading}
                                    className="bg-teal-500 hover:bg-teal-600"
                                >
                                    {serviceLoading ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <UserCheck className="w-4 h-4 mr-2" />
                                    )}
                                    Find Provider
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Active Service Requests */}
                {serviceRequests.filter(r => r.status !== 'completed' && r.status !== 'cancelled').length > 0 && (
                    <Card className="mb-8 border-teal-200 dark:border-teal-800">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <Stethoscope className="w-5 h-5 text-teal-500" />
                                    Active Requests
                                </CardTitle>
                                <Button variant="ghost" size="sm" onClick={fetchServiceRequests}>
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {serviceRequests.filter(r => r.status !== 'completed' && r.status !== 'cancelled').map((request) => (
                                    <div key={request.id} className="p-4 border rounded-xl bg-white dark:bg-slate-800">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className="font-medium capitalize">{request.service_type.replace('_', ' ')}</p>
                                                    {getStatusBadge(request.status)}
                                                    {request.urgency !== 'normal' && (
                                                        <Badge variant="outline" className={request.urgency === 'emergency' ? 'border-red-500 text-red-500' : 'border-amber-500 text-amber-500'}>
                                                            {request.urgency}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground">{request.description?.substring(0, 100)}...</p>
                                            </div>
                                        </div>
                                        
                                        {request.status === 'accepted' && request.assigned_provider_name && (
                                            <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg mb-3">
                                                <p className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    Assigned: {request.assigned_provider_name}
                                                </p>
                                                {request.assigned_provider_phone && (
                                                    <p className="text-sm text-green-600 mt-1">
                                                        📞 {request.assigned_provider_phone}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                                            <div className="flex items-center gap-4">
                                                {request.preferred_date && (
                                                    <span className="flex items-center gap-1">
                                                        <CalendarIcon className="w-4 h-4" />
                                                        {request.preferred_date}
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="w-4 h-4" />
                                                    {request.location?.postcode || request.location?.address?.substring(0, 20)}
                                                </span>
                                            </div>
                                            {request.status === 'pending' && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="text-red-500 hover:text-red-600"
                                                    onClick={() => cancelServiceRequest(request.id)}
                                                >
                                                    <X className="w-4 h-4 mr-1" /> Cancel
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle>Your Consultations</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {consultations.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No consultations booked yet</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {consultations.map((c, i) => (
                                    <div key={c.id || i} className="flex items-center justify-between p-4 border rounded-xl">
                                        <div className="flex items-center gap-4">
                                            {c.consultation_type === 'video' ? (
                                                <Video className="w-5 h-5 text-primary" />
                                            ) : c.consultation_type === 'home_visit' ? (
                                                <Home className="w-5 h-5 text-primary" />
                                            ) : (
                                                <Phone className="w-5 h-5 text-primary" />
                                            )}
                                            <div>
                                                <p className="font-medium">{c.preferred_date} at {c.preferred_time}</p>
                                                <p className="text-sm text-muted-foreground">{c.reason}</p>
                                                {c.consultation_type === 'home_visit' && c.address && (
                                                    <p className="text-xs text-primary flex items-center gap-1 mt-1">
                                                        <MapPin className="w-3 h-3" /> {c.address.substring(0, 50)}...
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {c.video_link && (
                                            <Button variant="outline" size="sm" onClick={() => window.open(c.video_link, '_blank')}>
                                                <ExternalLink className="w-4 h-4 mr-1" /> Join
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
